import React, { useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/auth';
import { useTheme as useAppTheme } from '../../context/ThemeContext';

declare global {
  interface Window {
    google: any;
  }
}

type ModalThemeMode = 'light' | 'dark';

const MODAL_THEME_MODE: ModalThemeMode = 'dark';

const getContainerStyles = (backgroundImage: string) => ({
  display: 'flex',
  justifyContent: { xs: 'center', md: 'flex-end' },
  alignItems: 'center',
  minHeight: '100vh',
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  px: { xs: 2, sm: 4, md: 8 },
  py: { xs: 4, md: 0 },
});

// TODO: Remove this forced dark modal once the company logo fits light mode backgrounds; right now the logo doesn't fit well with light mode.
const getPaperStyles = (mode: ModalThemeMode) => ({
  p: { xs: 3, sm: 4 },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 420,
  width: '100%',
  borderRadius: 3,
  boxShadow: mode === 'dark' ? '0 20px 50px rgba(0, 0, 0, 0.6)' : '0 20px 40px rgba(0, 0, 0, 0.15)',
  backgroundColor: mode === 'dark' ? 'rgba(18, 18, 18, 0.75)' : 'rgba(255, 255, 255, 0.85)',
  border: '1px solid rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(20px)',
});

const Login: React.FC = () => {
  const { login, loading, error, sessionMessage, clearSessionMessage, clearError } = useAuth();
  const { mode } = useAppTheme();

  const backgroundImage = useMemo(() => {
    const imageName = mode === 'dark'
      ? 'dark_model_login_background.png'
      : 'light_mode_login_background.png';
    return `${process.env.PUBLIC_URL}/resource/${imageName}`;
  }, [mode]);

  const handleCredentialResponse = useCallback((response: any) => {
    if (response.credential) {
      login(response.credential);
    }
  }, [login]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: '835518165659-m40hevu2r4cr6p9ev048idopq8a0ojsv.apps.googleusercontent.com',
          callback: handleCredentialResponse,
        });
        
        window.google.accounts.id.renderButton(
          document.getElementById('googleSignInButton'),
          { 
            theme: 'outline', 
            size: 'large',
            type: 'standard',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 280
          }
        );
      }
    };
    
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleCredentialResponse]);

  return (
    <Box sx={getContainerStyles(backgroundImage)}>
      <Paper
        elevation={6}
        sx={getPaperStyles(MODAL_THEME_MODE)}
      >
        <img
          src={process.env.PUBLIC_URL + '/transparent_logo_and_name.png'}
          alt="DecisionOpt"
          style={{ height: 140, marginBottom: 0, opacity: 1 }}
        />
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 500,
            mb: 0,
            color: MODAL_THEME_MODE === 'dark' ? '#ffffff' : '#101010',
          }}
        >
          ExPlan
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            color: MODAL_THEME_MODE === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            textAlign: 'center',
            fontWeight: 300,
            lineHeight: 1.1,
            letterSpacing: 0.2,
            marginBottom:3
          }}
        >
          Execute with Intent, Plan with Intelligence
        </Typography>
        
        
        {sessionMessage && (
          <Alert severity="info" sx={{ mb: 2, width: '100%' }} onClose={clearSessionMessage}>
            {sessionMessage}
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }} onClose={clearError}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <CircularProgress size={36} sx={{ mb: 2 }} />
        ) : (
          <div id="googleSignInButton"></div>
        )}
      </Paper>
    </Box>
  );
};

export default Login; 
